import { cva, type VariantProps } from 'class-variance-authority';

import { BUTTON_RADII_PX } from '../../styles/button-radii';
import { DARK_CONTROL } from '../../styles/elevation';

/**
 * Button visual variants — mirror Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`. Reference nodes:
 *   - 294:1855  – section, all 7 sizes × 5 variants × 5 states
 *   - 301:2499  – Primary / XXS / Default     (canonical filled-button anatomy)
 *   - 301:2501  – Primary / XXS / Hover
 *   - 301:2502  – Primary / XXS / Pressed
 *   - 301:2503  – Primary / XXS / Focused     (extra outer ring)
 *   - 301:2803  – Primary / XXS / Disabled    (default colors + opacity-30)
 *   - 369:1070  – dark-mode reference frame
 *
 * Axes:
 *   variant: primary | secondary | tertiary | success | alert
 *   size:    xxs (28px) | xs (32px) | s (36px) | m (40px) |
 *            l (44px)   | xl (48px) | xxl (56px)
 *   states:  default | hover | pressed | focused | disabled
 *
 * Filled-variant anatomy (primary / success / alert):
 *   – Asymmetric border: 1px top/sides + 2px bottom (`border border-b-2`),
 *     giving the button a thicker-bottom-edge bevel even before any shadow.
 *   – Padding compensation: `pt-px pb-[2px]` so the inner content row sits
 *     centered despite the asymmetric border (matches Figma's nested wrap
 *     structure expressed as a flat single element).
 *   – Combined drop + inset box-shadow via `shadow-btn-{variant}` — the
 *     preset packs both shadows into one CSS box-shadow value.
 *   – Pressed: collapse to symmetric `enabled:active:border-b` +
 *     `enabled:active:pb-px` and drop the shadow
 *     (`enabled:active:!shadow-none`). Padding shifts content down 1px to
 *     simulate the click depression. **All hover/active states scope to
 *     `:enabled`** so disabled buttons don't change colors on mouse-over.
 *
 *     NOTE (corrected): NativeWind does NOT drop `enabled:*` on native. Its
 *     selector normaliser switches on the pseudo-class kind and handles only
 *     `hover`/`active`/`focus`/`disabled`/`empty` — `:enabled` falls through
 *     with no `default` branch, so the rule SURVIVES carrying only its
 *     `:hover` / `:active` condition. `enabled:hover:*` and `enabled:active:*`
 *     are therefore live on native, ungated. Press is still safe because RN's
 *     Pressability suppresses `onPressIn` while `disabled`; HOVER has no such
 *     accident, so its disabled gate comes from `HoverablePressable.native`.
 *     The `native:active:*` lines below are retained duplicates of their
 *     `enabled:active:*` siblings (same values, harmless) — do not add more.
 *   – Focused: 1px outer ring flush against the button border, no
 *     transparent gap (`focus-visible:ring-1 ring-offset-0`). Ring shade
 *     is per-variant (Figma nodes 301:2527 / 451:6291 / 500:10711 /
 *     301:2542 / 301:2547):
 *       primary  → primary-950   (#2f0d68)
 *       secondary→ secondary-600 (#525252)
 *       tertiary → secondary-600 (#525252)
 *       success  → green-900           (#0d542b)
 *       alert    → red-900             (#82181a)
 *     Overrides the Pressable primitive's generic gray ring. Web-only —
 *     `focus-visible:` is a no-op on RN.
 *   – Disabled: same default colors + `!opacity-30` (per Figma node 301:2803).
 *
 * Outlined variants (secondary): no bevel, no inset highlight; subtle
 * low-α drop shadow (`shadow-btn-secondary`).
 *
 * Glass variant (tertiary): secondary's translucent fill and web blur with a
 * lighter 1px border and NO shadow or bevel — so a tertiary sits optically
 * level in a row where a secondary would sit 1px lower.
 *
 * Ghost variant (ghost): no border / bg / shadow; uses a transparent border so
 * size geometry stays identical to the other variants.
 *
 * Dark-mode strategy: the whole palette auto-inverts via CSS vars (light in
 * `:root`, the exact mirror in `.dark` / `.dark:root` — see
 * `generate-css-vars.cjs`). So variants carry NO `dark:` shade swaps: e.g.
 * `bg-secondary-50` renders white in light and its mirror (secondary-950) in
 * dark automatically. Works identically on web (`.dark` class) and on mobile
 * (NativeWind, `.dark:root`).
 */
export const buttonVariants = cva(
  // Base: layout + a safe transition utility + the focus-ring SHAPE shared
  // by every variant. State-transition feedback (hover / pressed / focused)
  // animates on web via Tailwind; native uses Pressable's built-in pressed
  // feedback. The focus ring is supplied by the Pressable primitive (web
  // file only — see Pressable.tsx).
  //
  // The `web:focus-visible:ring-1 web:focus-visible:ring-offset-0` pair is
  // identical across all variants (and across composers like SrButton), so
  // it lives here once instead of being redeclared per variant. Each variant
  // only adds its `web:focus-visible:ring-{color}` to colour the ring;
  // composers override the colour via `!ring-[#...]` since twMerge can't
  // dedupe the `web:`-prefixed classes across two CVAs.
  //
  // `web:` keeps these to the browser — NativeWind v4 would otherwise drop
  // the `focus-visible:` modifier on native and apply the `ring-*` shadow
  // unconditionally (visible as a dark stroke at the rounded corners on iOS).
  //
  // `group relative` enables the inset-highlight overlay (rendered as a
  // child in Button.tsx) to read the Pressable's `:active` / pressed state
  // via `group-active:hidden`.
  'group relative inline-flex flex-row items-center justify-center border ' +
    // Web-only animation utilities. `transition-*`, `transform-*`,
    // `duration-*`, and `ease-*` are gated behind `web:` because
    // NativeWind v4 on native interprets them by upgrading the host to a
    // Reanimated-driven animated component, and that upgrade trips
    // `TypeError: cannot add a new property` on RN 0.83 (new arch) +
    // Hermes — see `Pressable.native.tsx` and
    // `useButtonAnimation.native.ts`. The native press SCALE is supplied
    // imperatively instead — Button passes `pressScale={0.97}` to the
    // Pressable primitive, which drives an RN-core Animated transform
    // (150ms ease-out) outside NativeWind's machinery. The bevel fade
    // stays class-driven (`group-active:opacity-0`, instant).
    'web:transform-gpu ' +
    'web:transition-[transform,colors,box-shadow,opacity] web:duration-150 web:ease-out ' +
    'web:enabled:active:scale-[0.97] ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0',
  {
    variants: {
      variant: {
        primary:
          // Asymmetric bevel: 1px top/sides + 2px bottom (`border border-b-2`)
          // — paired with `pt-px pb-[2px]` to keep content centered against
          // the thicker bottom edge. Pressed state collapses the bevel back
          // to symmetric `border-b` + `pb-px` and drops the shadow. The
          // BevelHighlight SVG supplies the matching top gloss; together
          // they give the chip its 3D depth.
          //
          // Focus-ring shape (`ring-1` + `ring-offset-0`) is in the CVA
          // base — variants only contribute the per-palette ring colour.
          'border-b-2 pt-px pb-[2px] ' +
          'bg-stable-primary-600 border-stable-primary-700 ' +
          'enabled:hover:bg-stable-primary-700 enabled:hover:border-stable-primary-800 ' +
          'enabled:active:bg-stable-primary-700 enabled:active:border-stable-primary-800 ' +
          'enabled:active:border-b enabled:active:pb-px enabled:active:!shadow-none ' +
          'native:active:bg-stable-primary-700 native:active:border-stable-primary-800 ' +
          'native:active:border-b native:active:pb-px ' +
          'web:focus-visible:ring-primary-950 ' +
          'shadow-btn-primary',
        // Outlined surface with the SAME asymmetric bevel as primary, just
        // with the secondary palette and a smaller low-α drop shadow
        // (no inset highlight). Figma node 301:2500. Dark mode is automatic:
        // `bg-secondary-50` (white) auto-inverts to secondary-950 in dark.
        secondary:
          // Same asymmetric bevel as primary, secondary palette.
          //
          // Pressed tones come from Figma node 451:6181 — fill steps one
          // shade darker than default (-50 → -100) and the BORDER STAYS at
          // the default shade. We previously pushed both bg and border two
          // shades on press (-50 → -200, -200 → -400), which read as a much
          // louder "pressed" than Figma intends. The press should feel
          // subtle — just the bevel collapsing + the shadow dropping — with
          // the fill tone shift acting as a quiet confirm rather than a
          // dramatic recolour. (Dark mode mirrors these shades automatically.)
          'border-b-2 pt-px pb-[2px] ' +
          // Glass (Figma DS "Glass" effect, node 3382:5752): the fill is the
          // `brand/secondary/30` token = rgba(255,255,255,.7) in light —
          // expressed as `secondary-50/70` so it auto-inverts to a translucent
          // dark in dark mode — over a 6px backdrop blur. `backdrop-blur` is
          // web-only (`web:`); native keeps the translucent fill but has no
          // backdrop-filter, so true blur there awaits a BlurView.
          'bg-secondary-50/70 web:backdrop-blur-[6px] border-secondary-200 ' +
          'enabled:hover:bg-secondary-100/80 enabled:hover:border-secondary-300 ' +
          'enabled:active:bg-secondary-100/80 ' +
          // Dark: a CONTROL — one clear step above the surface it sits on,
          // never the mirrored -50 hole (elevation.ts, design review 2026-10);
          // hover/press step up from there instead of down.
          `${DARK_CONTROL} dark:enabled:hover:bg-secondary-200/80 dark:enabled:active:bg-secondary-200/80 ` +
          'enabled:active:border-b enabled:active:pb-px enabled:active:!shadow-none ' +
          'native:active:bg-secondary-100/80 ' +
          'native:active:border-b native:active:pb-px ' +
          'web:focus-visible:ring-secondary-600 ' +
          'shadow-btn-secondary',
        // Subtle outlined — same surface as secondary but with a much
        // lighter 1px symmetric border, no shadow, no bevel. Figma node
        // 301:2509. Text matches secondary.
        tertiary:
          // Same glass treatment as secondary (translucent fill + web blur),
          // with tertiary's lighter 1px border and no shadow/bevel.
          'bg-secondary-50/70 web:backdrop-blur-[6px] border-secondary-100 ' +
          'enabled:hover:bg-secondary-100/80 enabled:hover:border-secondary-200 ' +
          'enabled:active:bg-secondary-200/80 enabled:active:border-secondary-300 ' +
          `${DARK_CONTROL} dark:enabled:hover:bg-secondary-200/80 dark:enabled:active:bg-secondary-300/60 ` +
          'native:active:bg-secondary-200/80 native:active:border-secondary-300 ' +
          'web:focus-visible:ring-secondary-600 ',
        // Ghost — fully transparent at rest (no fill, transparent 1px
        // border). On hover a SOLID secondary-100 fill materialises with NO
        // stroke; pressed darkens one solid step to secondary-200. The border
        // stays transparent in every state (never a visible stroke) — the 1px
        // transparent border only preserves box geometry so layout never
        // shifts. No glass/blur (opaque fill = nothing to blur behind), no
        // bevel, no shadow, no inset highlight. (Design spec, 2026-07-10: the
        // earlier translucent secondary-50/70 "glass" fill + secondary-200
        // stroke read as a white fill with a border — not per DS.)
        //
        // Use for low-emphasis chrome — Cancel / Back / Skip in dialogs,
        // breadcrumb crumbs, toast dismiss X, tip-tap toolbar icons, the
        // inactive tab in a segmented control, etc. Pairs with `tertiary`
        // (which is the always-visible soft-chip equivalent).
        ghost:
          'bg-transparent border-transparent ' +
          // Hover is the CONTROL step (`secondary-200` at 60%), relative to
          // whatever the button sits on — a solid `secondary-100` vanished on
          // every surface that already is that colour (owner, 2026-10).
          'enabled:hover:bg-secondary-200/60 ' +
          'enabled:active:bg-secondary-200 ' +
          'native:active:bg-secondary-200 ' +
          'web:focus-visible:bg-secondary-200/60 ' +
          'web:focus-visible:ring-secondary-600 ',
        // Same frozen-resting / inverting-interactive split as primary (see that
        // comment): resting fill+border + label stay inline hex; hover/pressed
        // + focus-ring use green tokens so they whiten in dark mode.
        success:
          'border-b-2 pt-px pb-[2px] ' +
          'bg-stable-green-700 border-stable-green-800 ' +
          'enabled:hover:bg-stable-green-800 enabled:hover:border-stable-green-900 ' +
          'enabled:active:bg-stable-green-800 enabled:active:border-stable-green-900 ' +
          'enabled:active:border-b enabled:active:pb-px enabled:active:!shadow-none ' +
          'native:active:bg-stable-green-800 native:active:border-stable-green-900 ' +
          'native:active:border-b native:active:pb-px ' +
          'web:focus-visible:ring-green-900 ' +
          'shadow-btn-success',
        // Same split as primary, red palette.
        alert:
          'border-b-2 pt-px pb-[2px] ' +
          'bg-stable-red-600 border-stable-red-700 ' +
          'enabled:hover:bg-stable-red-700 enabled:hover:border-stable-red-800 ' +
          'enabled:active:bg-stable-red-700 enabled:active:border-stable-red-800 ' +
          'enabled:active:border-b enabled:active:pb-px enabled:active:!shadow-none ' +
          'native:active:bg-stable-red-700 native:active:border-stable-red-800 ' +
          'native:active:border-b native:active:pb-px ' +
          'web:focus-visible:ring-red-900 ' +
          'shadow-btn-alert',
      },
      // Per-size geometry verified node-by-node against Figma "Button
      // (Label +Text)" (file YZK9r3wDhvw1pY60KlVEIj): xxs 301:2499 ·
      // xs 301:2524 · s 301:2549 · m 301:2574 · l 301:2599 · xl 301:2624 ·
      // xxl 301:2649. Heights are exact (h-*).
      //
      // Horizontal padding: each Figma node nests an outer border-frame (1px
      // border + a 1px `px-px` inset) around an inner content row that holds
      // the real per-size padding. This flat Button has no inner wrapper, so
      // it FOLDS that 1px inset into pl/pr — pl = figmaContentLeft + 1,
      // pr = figmaContentRight + 1. Figma content L/R → folded value used here:
      //   xxs 7/9→8/10 · xs 8/10→9/11 · s 10/12→11/13 · m 10/12→11/13 ·
      //   l 12/14→13/15 · xl 14/16→15/17 · xxl 16/18→17/19.
      // (s and m share padding in Figma — the m step-up is height + type size,
      // not padding.) Bracket px rather than the Tailwind step scale: the
      // folded values are odd and must hit the spec to the pixel.
      //
      // gap-* is the icon↔label spacing — also per-size in Figma (xxs 4 ·
      // xs/s/m 6 · l/xl/xxl 8). Declared here, NOT in the CVA base, so each
      // size pins its own gap; the default `m` keeps the historical 6px.
      //
      // Outer corners are uniform now that the bevel border has been
      // dropped — all four corners share the `outer-top` radius (= inner
      // + 1), so the visible inner curve still lands one pixel inside the
      // 1px border. The `-inner` and `-outer-bottom` tokens stay in the
      // preset for the inset wrapper (`-inner`) and any future re-introduction
      // of the asymmetric bevel.
      // `min-h-*` pins the band height as a floor so the chip can never
      // collapse below spec. A bare `h-*` is the flex *main* size, which a
      // caller's `flex-1` (→ `flex: 1 1 0%`) overrides inside a `flex-col`
      // parent — the default `min-height: auto` then shrinks the button to
      // its content (~26px) instead of the band height. `min-h-*` overrides
      // that auto minimum, so the height survives any flex context (e.g.
      // dropdown triggers that stretch to fill a column). No-op for a
      // normally-flowing button; purely defensive.
      size: {
        xxs: 'h-7  min-h-7  gap-1   pl-[8px]  pr-[10px] rounded-btn-xxs-outer-top',
        xs: 'h-8  min-h-8  gap-1.5 pl-[9px]  pr-[11px] rounded-btn-xs-outer-top',
        s: 'h-9  min-h-9  gap-1.5 pl-[11px] pr-[13px] rounded-btn-s-outer-top',
        m: 'h-10 min-h-10 gap-1.5 pl-[11px] pr-[13px] rounded-btn-m-outer-top',
        l: 'h-11 min-h-11 gap-2   pl-[13px] pr-[15px] rounded-btn-l-outer-top',
        xl: 'h-12 min-h-12 gap-2   pl-[15px] pr-[17px] rounded-btn-xl-outer-top',
        xxl: 'h-14 min-h-14 gap-2   pl-[17px] pr-[19px] rounded-btn-xxl-outer-top',
      },
      iconOnly: {
        true: '',
        false: '',
      },
      disabled: {
        true: '',
        false: '',
      },
      // Keybind chip present with NO leading icon/slot: Figma's Keybind=Yes
      // Icon=No nodes use SYMMETRIC content padding (m: 10/10 vs 10/12 on
      // Keybind=No — the chip's own visual weight replaces the extra right
      // padding). With a left icon the normal asymmetric padding returns
      // (Icon=Yes Keybind=Yes m keeps 10/12). Computed by Button.tsx;
      // per-size folded pr overrides live in compoundVariants below.
      keybindPad: {
        true: '',
        false: '',
      },
      // Stretches the button to fill its parent's width. Opt-in (default
      // off) so the same JSX renders identically on web and native — see
      // `recipes/use-button.md` for when to enable. Combined with the
      // iconOnly compound so an icon-only button stays square even if
      // `fullWidth` is accidentally set.
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      // ─── iconOnly square shapes (override pl-/pr- with matching w-) ─────
      { iconOnly: true, size: 'xxs', class: 'w-7  px-0' },
      { iconOnly: true, size: 'xs', class: 'w-8  px-0' },
      { iconOnly: true, size: 's', class: 'w-9  px-0' },
      { iconOnly: true, size: 'm', class: 'w-10 px-0' },
      { iconOnly: true, size: 'l', class: 'w-11 px-0' },
      { iconOnly: true, size: 'xl', class: 'w-12 px-0' },
      { iconOnly: true, size: 'xxl', class: 'w-14 px-0' },

      // ─── fullWidth must NOT apply to icon-only — those stay square.
      // The compound clears `w-full` back to `w-{size}` if both are set.
      { iconOnly: true, fullWidth: true, size: 'xxs', class: '!w-7' },
      { iconOnly: true, fullWidth: true, size: 'xs', class: '!w-8' },
      { iconOnly: true, fullWidth: true, size: 's', class: '!w-9' },
      { iconOnly: true, fullWidth: true, size: 'm', class: '!w-10' },
      { iconOnly: true, fullWidth: true, size: 'l', class: '!w-11' },
      { iconOnly: true, fullWidth: true, size: 'xl', class: '!w-12' },
      { iconOnly: true, fullWidth: true, size: 'xxl', class: '!w-14' },

      // ─── keybindPad: symmetric pr (= pl) when a keybind chip is the only
      // trailing content. Folded values (figma content pad + 1px border
      // inset), same fold model as the size variant's pl/pr.
      { keybindPad: true, size: 'xxs', class: 'pr-[8px]' },
      { keybindPad: true, size: 'xs', class: 'pr-[9px]' },
      { keybindPad: true, size: 's', class: 'pr-[11px]' },
      { keybindPad: true, size: 'm', class: 'pr-[11px]' },
      { keybindPad: true, size: 'l', class: 'pr-[13px]' },
      { keybindPad: true, size: 'xl', class: 'pr-[15px]' },
      { keybindPad: true, size: 'xxl', class: 'pr-[17px]' },

      // ─── Disabled: keep default colors + opacity 30. Per Figma node
      // 301:2803 — disabled is a pure opacity treatment, no tone-shift.
      // Browsers don't fire :hover/:active on disabled <button>s. On native,
      // RN blocks press feedback while disabled, and `HoverablePressable`
      // blocks hover — the latter is load-bearing, NOT defensive, because
      // `enabled:` carries no weight on native (see the note up top). So the
      // hover/active CVA classes don't apply at runtime on either platform —
      // no per-variant overrides needed.
      {
        disabled: true,
        class: '!opacity-30',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'm',
      iconOnly: false,
      disabled: false,
      fullWidth: false,
      keybindPad: false,
    },
  },
);

/*
 * Keybind chip CVAs moved to `../KeybindChip/KeybindChip.tsx` (shared with
 * SegmentedControl and Tooltip/Popover) — Button.tsx renders `<KeybindChip>`.
 */

/**
 * Keybind chip — the small keycap rendered after the label when
 * `keybinding` is set (Figma "Shortcut" component, Keybind=Yes variants,
 * light 301:2674 / dark 3913:31435; per-size chips: xxs/xs/s share the
 * "Small" chip, m–xxl the "Default" chip — verified 3900:21641 / 3901:22338 /
 * 3901:23023 / 3901:23678 / 3905:29192 / 3905:29890 / 3905:30472).
 *
 * Anatomy: NO shadow of its own — the raised-keycap look is purely the
 * asymmetric border (thicker bottom: 1.4/1.8px vs 0.6/0.8px top+sides)
 * plus a bottom padding that lifts the glyph. Width hugs content with a
 * square `min-w == h` floor, so a single letter renders as a square key.
 *
 * State-invariant: the chip is byte-identical across default / hover /
 * pressed / focused in Figma; disabled washes it via the button-level
 * `!opacity-30`. So no state classes here.
 *
 * Colors: primary / success / alert chips are MODE-INVARIANT in Figma
 * (same hex in dark mode) → frozen inline hex, matching the filled
 * variants' frozen-fill strategy. Secondary / tertiary / ghost chips use
 * the auto-inverting secondary tokens (light #f5f5f5/#a1a1a1/#262626 →
 * dark #171717/#525252/#e5e5e5), exactly per the dark Figma frame.
 * Note: success's border is green-500, not the -400 step the other
 * palettes use — faithful to Figma, not a typo.
 */
export const buttonKeybindVariants = cva(
  'flex flex-row items-center justify-center overflow-hidden border-solid shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-stable-primary-100 border-stable-primary-400',
        secondary: 'bg-secondary-100 border-secondary-400',
        tertiary: 'bg-secondary-100 border-secondary-400',
        ghost: 'bg-secondary-100 border-secondary-400',
        success: 'bg-stable-green-100 border-stable-green-500',
        alert: 'bg-stable-red-100 border-stable-red-400',
      },
      // Two chip tiers, not seven: Figma reuses "Small" for xxs/xs/s and
      // "Default" for m/l/xl/xxl. Button.tsx maps button size → tier.
      chipSize: {
        sm: 'h-4 min-w-4 rounded-[4.8px] border-[0.6px] border-b-[1.4px] px-1 pb-[1.2px]',
        md: 'h-5 min-w-5 rounded-[6px] border-[0.8px] border-b-[1.8px] px-1 pb-[1.5px]',
      },
    },
    defaultVariants: { variant: 'primary', chipSize: 'md' },
  },
);

/**
 * Keybind chip text — Chivo Mono Medium, uppercase, per Figma "Shortcut".
 * `font-chivo-mono` resolves via the shared preset; the face itself is
 * only loaded on web (flashka-web index.css) — the chip never renders on
 * native (see Button.tsx).
 */
export const buttonKeybindTextVariants = cva(
  'whitespace-nowrap text-center font-chivo-mono font-medium uppercase leading-[normal]',
  {
    variants: {
      variant: {
        primary: 'text-stable-primary-600',
        secondary: 'text-secondary-800',
        tertiary: 'text-secondary-800',
        ghost: 'text-secondary-800',
        success: 'text-stable-green-600',
        alert: 'text-stable-red-600',
      },
      chipSize: {
        sm: 'text-[10px] tracking-[-0.2px]',
        md: 'text-[14px] tracking-[-0.42px]',
      },
    },
    defaultVariants: { variant: 'primary', chipSize: 'md' },
  },
);

/**
 * Text classes per variant + size. Applied to the inner <Text> wrapping a
 * string child. Also colors icons that read from `currentColor` on web.
 */
export const buttonTextVariants = cva('whitespace-nowrap font-medium !leading-[normal]', {
  variants: {
    variant: {
      // Filled variants sit on a saturated, NON-inverting fill (bg-{c}-500 is
      // the mirror midpoint, so it's the same hex in light + dark). Their text
      // must therefore stay light in both modes — use a fixed hex (matches the
      // stable icon colors in Button.icons.ts) instead of an auto-inverting
      // `text-{c}-50` token, which would flip to a dark shade in dark mode and
      // become unreadable on the fill.
      primary: 'text-stable-primary-50',
      // Outlined/ghost variants invert WITH their surface: secondary-600
      // (#525252) in light auto-inverts to secondary-400 (#a1a1a1) in dark —
      // readable on the inverted surface, matching Figma's dark spec.
      secondary: 'text-secondary-600 ',
      tertiary: 'text-secondary-600 ',
      ghost: 'text-secondary-600 ',
      success: 'text-stable-green-50',
      alert: 'text-stable-red-50',
    },
    // Typography per Figma per-size band. `!leading-[normal]` (base) lets
    // RN's Text use font metrics — matches web rendering and avoids the
    // ascender clipping that `leading-none` caused on native.
    //
    // Native font-family: web Mona Sans is a variable font and respects
    // `font-medium` / `font-bold` automatically. On native, custom .ttf
    // files load per-weight and CSS `font-weight` doesn't swap them —
    // explicit `native:font-mona-{medium,bold}` classes pin the correct
    // family. Those classes are no-ops on web.
    size: {
      xxs: 'text-xs native:font-mona-medium',
      xs: 'text-sm native:font-mona-medium',
      s: 'text-sm native:font-mona-medium',
      m: 'text-base native:font-mona-medium',
      l: 'text-base native:font-mona-medium',
      xl: 'text-lg !font-semibold native:font-mona-semibold',
      xxl: 'text-lg !font-semibold native:font-mona-semibold',
    },
    disabled: {
      true: '',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'm',
    disabled: false,
  },
});

/**
 * Inset-highlight overlay wrapper — clips a thin top "bevel glow" strip to
 * the Pressable's rounded shape, cross-platform. Used by filled variants
 * (primary / success / alert) to replicate the Figma "Inner-Button" effect
 * (`inset 0 2px 2px 0 rgba(245,243,255,0.30)`) without depending on CSS
 * inset box-shadows (which NativeWind v4 can't apply on RN).
 *
 * The wrapper is `absolute inset-0 overflow-hidden` — flush against the
 * button's border with no inset gap. Button.tsx supplies the matching
 * `borderRadius = scaled.inner` (the chip's actual inner-curve radius)
 * via inline `style` + `nativeProps.style`, so the highlight traces the
 * border's inner edge exactly and reads as a real bevel rather than a
 * floating strip.
 *
 * The per-size `rounded-btn-{size}-inner` className remains as a CSS
 * fallback that renders before the inline style hydrates (matters on web
 * SSR; on native it's already overridden by `useScaledButtonRadii`).
 */
export const buttonInsetWrapperVariants = cva(
  'pointer-events-none absolute inset-0 overflow-hidden',
  {
    variants: {
      size: {
        xxs: 'rounded-btn-xxs-inner',
        xs: 'rounded-btn-xs-inner',
        s: 'rounded-btn-s-inner',
        m: 'rounded-btn-m-inner',
        l: 'rounded-btn-l-inner',
        xl: 'rounded-btn-xl-inner',
        xxl: 'rounded-btn-xxl-inner',
      },
    },
    defaultVariants: { size: 'm' },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

/**
 * Inner-curve radius (in pixels) per button size — derived from the JSON
 * source-of-truth in `../../styles/button-radii.json`. Native code that
 * can't introspect Tailwind classes (e.g. the BevelHighlight SVG) reads
 * this map to stroke a rounded rect that traces the button's inner curve.
 *
 * Single source of truth: see `BUTTON_RADII_PX` in
 * `../../styles/button-radii.ts`. The Tailwind preset reads from the same
 * JSON via `./button-radii.cjs`, so this map literally cannot drift from
 * the rendered CSS.
 *
 * @deprecated for Button.tsx — prefer `useScaledButtonRadii(size).inner`
 *   which also incorporates the runtime responsive scale factor. Kept as
 *   an export for external consumers that need a static (unscaled) value.
 */
export const BUTTON_RADIUS_PX = Object.fromEntries(
  Object.entries(BUTTON_RADII_PX).map(([k, v]) => [k, v.inner]),
) as Record<NonNullable<ButtonVariantProps['size']>, number>;
